/**
 * The pieces the four raid-family missions share: what a raid goes after, what
 * flies it, what flies against it, and what has to be said about the parts of it
 * this port made up.
 *
 * They are one file because they are one mission seen from four seats. Every one
 * of Escort a Bombing Raid, Stop a Bombing Raid, Intercept a Zeppelin and
 * Balloon Defense is *something inbound to a target that must or must not be
 * reached*, and the port has exactly one machinery for that: a strike force with
 * `Bomb` orders (`combat/bombs.ts`), destructible ground targets
 * (`combat/target-profiles.ts`), and a raid outcome read off both
 * (`mission/raid.ts`). The generators themselves are in `generators-raid.ts`
 * (the two offensive/defensive bomber missions) and `generators-defence.ts`
 * (the Zeppelin interception and the balloon defence).
 *
 * ## The numbers that are the game's own
 *
 * - **The bomb run is flown at 1,200 ft.** *"To be sure of getting a hit,
 *   bombers will make their attack run at 1200 feet. When setting up an intercept
 *   mission, don't put the intercepting scouts too high."*
 *   (docs/game/manual/05-mission-builder.md) — `bombRunAltitudeM`.
 * - **A flight is up to four aircraft.** *"You may select up to four aircraft."*
 *   (docs/game/manual/04-gameplay.md, All Other Mission Types).
 * - **Balloons sit at 5,000 ft or lower.** *"When placing balloons, keep their
 *   altitude at 5000 feet or lower. If the balloons are too high, they will be
 *   above their AA defenses."* — which this port takes as a ceiling, and then
 *   flies lower still for the reason `generators-sortie.ts` states.
 * - **One Zeppelin.** `RBMB.EXE`: *"You can have only one zeppelin."*
 * - **Escorting is a formation problem, not a navigation one.** *"On escort
 *   missions, you will not need to navigate … You will only need to follow the
 *   aircraft you're escorting."*
 *
 * ## What is port-side
 *
 * The pass mark for a raid (`RAID_SUCCESS_FRACTION`), the bomb loads, and the
 * choice of which shipped scenery object stands in for a factory. None of the
 * three is decodable, each carries a note below, and every mission that uses one
 * prints that note on its briefing card.
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import type { Vec3 } from "../flight/math3d.ts";
import { DEFAULT_TUNABLES } from "../flight/tunables.ts";
import type { Waypoint } from "../combat/route.ts";
import { groundTargetPosition } from "../combat/targets.ts";
import { buildFlight, briefingLines, waypoint } from "./mission-build.ts";
import type { MissionContext } from "./mission-options.ts";
import type { BombLoadSpec } from "../combat/encounter.ts";
import type { MissionFlight, MissionTarget } from "./mission-spec.ts";

/** `bombRunAltitudeM` — the Mission Builder manual's 1,200 ft, in metres. */
export const BOMB_RUN_ALTITUDE_M = DEFAULT_TUNABLES.bombRunAltitudeM;
/** The escort sits above its charges, where a fighter escort always did. */
export const ESCORT_STACK_M = 350;
/** The manual's own flight ceiling: "You may select up to four aircraft." */
export const MAX_FLIGHT_AIRCRAFT = 4;
/** Where an interception forms up, above the inbound raid. */
export const INTERCEPT_STACK_M = 500;
/** Zeppelins raided high; this family's airship is on its way in, not on its way home. */
export const ZEPPELIN_RAID_ALTITUDE_M = 2400;

export const RAID_PASS_NOTE =
  "How much of a raid has to get through for it to count is port-side: docs/game/missions.md lists the raid types' exact win conditions as an open question that would need play-testing the original under DOSBox. This port's rule is stated in `mission/raid.ts` — half the targets down.";

export const BOMB_LOAD_NOTE =
  "Bomb loads are the manual's own aircraft-table figures (Gotha \"up to 1,100 lb\", Handley Page \"2,000 lb payload\") read as a count of bombs. No decoded file carries a bomb field at all — `APLANE.DAT` has none — so the number of bombs, what one does and how wide it does it are all declared tunables rather than transcribed values.";

export const GROUND_MODEL_NOTE =
  "The raid's targets are drawn with real shipped scenery, but *which* `TERR1.TBL` slot is a factory and which is a hangar is not decoded (world-terrain.md §2.4 is open on it), so the model each target uses was chosen by measured size and is a port-side stand-in, not an identification.";

export const FLAK_NOTE =
  "Anti-aircraft fire is the standing belt along the front (AD-010 §9, `mission/flak-placement.ts`), placed at the original's own density of one battery per 7.4 km of line. The raid *target* itself carries no extra guns: the manual promises \"There will always be flack over enemy cities\", but the original authors every emplacement into its scenario stream and this port has not decoded a shipped city raid to copy, so a target beyond the belt's reach is defended only by the aeroplanes over it.";

/**
 * Trains, added to the raid family alongside the patrol's (user decision,
 * 2026-08-23; `docs/game/ground-traffic.md`). A raid's own target site is a
 * real ground target already on the map, exactly the kind of rear-area ground
 * the original's class-5 trains run behind, so it is not scenery this port
 * invented for the occasion.
 */
export const RAID_TRAIN_NOTE =
  "Enemy — or, on Stop a Bombing Raid, your own — trains run the lines of communication behind this raid's own target site: the original's object class 5, recovered whole (docs/game/ground-traffic.md). Their placement is drawn from a separate salted stream (`:traffic`) so adding them could not shift a single weather roll or aeroplane pick anywhere else in the mission. A ground kill is never a victory, on a train the same as on the yard beside it.";

export const RAID_NOTES: readonly string[] = [RAID_PASS_NOTE, BOMB_LOAD_NOTE, GROUND_MODEL_NOTE, FLAK_NOTE, RAID_TRAIN_NOTE];

/**
 * Printed on the card of any mission that actually handed the player a rack, so
 * nobody mistakes it for something the original did.
 */
export const PLAYER_BOMB_NOTE =
  "You are carrying bombs, which is a **port extension**: Red Baron never arms the player with anything but guns — no bomb key, no loadout screen, no flyable two-seater (docs/game/player-bombing.md has the evidence). The rack, the release key and the aiming are this port's, opt-in and off unless asked for.";

/**
 * The player's own load, or nothing at all when he was not given one. The
 * aiming list is the raid's, which the release path never reads — a hand-thrown
 * bomb goes where the pilot lets it go (`combat/bomb-pass.ts`) — but it records
 * what he was sent to, the same way a bomber's does.
 */
export function playerBombLoad(context: MissionContext): BombLoadSpec | undefined {
  if (context.playerBombs <= 0) return undefined;
  return { count: context.playerBombs, targetIds: TARGET_IDS };
}

/** A bomber's rack, from the manual's payload figures. See `BOMB_LOAD_NOTE`. */
const BOMB_LOADS: Readonly<Partial<Record<AircraftKey, number>>> = { GOTHA: 4, HANDLEY: 6, DH4: 2 };

/** The heavy each side sent. The Gotha is the German raider; the Handley Page is the British night heavy. */
export function bomberTypeFor(nation: "allied" | "german"): AircraftKey {
  return nation === "german" ? "GOTHA" : "HANDLEY";
}

export function bomberNameOf(aircraft: AircraftKey): string {
  return aircraft === "GOTHA" ? "Gotha" : "Handley Page";
}

export interface StrikeSite {
  /** Where the target cluster sits, in the mission's `z` convention. */
  readonly line: number;
  readonly spread: number;
}

export const TARGET_IDS: readonly string[] = ["target-1", "target-2", "target-3"];

/** The three structures the manual names a raid going after: "RR yards, factories, and supply dumps". */
export function raidTargets(site: StrikeSite, side: "friendly" | "hostile"): readonly MissionTarget[] {
  return [
    {
      id: "target-1",
      kind: "railYard",
      side,
      position: groundTargetPosition("railYard", -site.spread, site.line),
      headingRad: Math.PI / 2,
      label: "Railway yard",
    },
    {
      id: "target-2",
      kind: "factory",
      side,
      position: groundTargetPosition("factory", 0, site.line - 220),
      headingRad: 0,
      label: "Factory",
    },
    {
      id: "target-3",
      kind: "supplyDump",
      side,
      position: groundTargetPosition("supplyDump", site.spread, site.line + 160),
      headingRad: 0,
      label: "Supply dump",
    },
  ];
}

export interface BomberFormation {
  readonly count: number;
  readonly aircraft: AircraftKey;
  readonly side: "friendly" | "hostile";
  /** Where the formation starts. */
  readonly startZ: number;
  readonly homeZ: number;
  readonly callsign: string;
  /** The raid's targets, in `TARGET_IDS` order — one bomber lines up on each. */
  readonly targets: readonly MissionTarget[];
}

/**
 * The raid itself: heavies under `defend` orders, flying in at the manual's
 * 1,200 ft. They never manoeuvre to fight — their gunners work the arcs the
 * fleet gun table gives them (three stations on both heavies) while the pilot
 * holds the run.
 *
 * Each bomber gets the aiming list **rotated onto its own target** and a
 * waypoint over that target, so a raid of three spreads across the yard, the
 * factory and the dump instead of three aeroplanes queueing for the same
 * building. A bomber whose target is already alight moves to the next on its
 * list; it does not re-plan its route to match, which is a real limitation and
 * is stated in the missions' port notes.
 */
export function bomberFlights(formation: BomberFormation): MissionFlight[] {
  return Array.from({ length: formation.count }, (_unused, index) => {
    const rotation = index % TARGET_IDS.length;
    const aimList = [...TARGET_IDS.slice(rotation), ...TARGET_IDS.slice(0, rotation)];
    const primary = formation.targets[index % formation.targets.length];
    if (!primary) throw new Error("a bombing raid needs at least one target");
    return buildFlight({
      id: `bomber-${index + 1}`,
      side: formation.side,
      aircraft: formation.aircraft,
      callsign: `${formation.callsign} ${index + 1}`,
      position: {
        x: primary.position.x + (index - (formation.count - 1) / 2) * 120,
        y: BOMB_RUN_ALTITUDE_M + index * 40,
        z: formation.startZ,
      },
      headingRad: formation.startZ > primary.position.z ? 0 : Math.PI,
      skill: "regular",
      routeOrders: "defend",
      bombs: { count: BOMB_LOADS[formation.aircraft] ?? 2, targetIds: aimList },
      waypoints: [
        // The run is flown *through* the target: the release happens roughly a
        // fall's travel short of it, so a waypoint short of the building would
        // have the formation turn for home before the lever moved.
        //
        // `bombRun` is what makes "through" true (AD-007k §10). Without it the
        // waypoint captured 400 m out and the formation turned for home while
        // the aiming solution was still improving — the release was taken in the
        // turn-away, and a third of the stick never left the rack.
        { ...waypoint({ x: primary.position.x, y: BOMB_RUN_ALTITUDE_M, z: primary.position.z }, "TARGET"), bombRun: true },
        waypoint({ x: primary.position.x, y: BOMB_RUN_ALTITUDE_M + 200, z: formation.homeZ }, "HOME"),
      ],
    });
  });
}

export function bomberIdsFor(count: number): readonly string[] {
  return Array.from({ length: count }, (_unused, index) => `bomber-${index + 1}`);
}

export interface PlayerSection {
  readonly context: MissionContext;
  readonly position: Vec3;
  readonly headingRad: number;
  /** How many fly with him. The manual's ceiling for the whole flight is four. */
  readonly wingmen: number;
  readonly waypoints: readonly Waypoint[];
  readonly ammunition?: "regular" | "incendiary";
  /**
   * A rack for the **player only** — never his wingmen, who are flown by the
   * bombing AI and would turn an escort into a second raid. Omitted unless the
   * caller asked for one (`MissionOptions.playerBombs`, a port extension:
   * docs/game/player-bombing.md).
   */
  readonly playerBombs?: BombLoadSpec;
}

/**
 * The player's *flight*, not his aeroplane. The manual is explicit that every
 * mission type but the two Dogfight modes lets you take company — *"You may
 * select up to four aircraft"* — and the raid family is the reason that matters:
 * one scout sent against three heavies and their escort is not a mission the
 * original would have briefed, and the port's other five types got away without
 * wingmen only because they are duels and sweeps.
 */
export function playerSection(section: PlayerSection): MissionFlight[] {
  const { context, position } = section;
  const wingmen = Math.min(section.wingmen, MAX_FLIGHT_AIRCRAFT - 1);
  return [
    buildFlight({
      id: "player",
      side: "friendly",
      aircraft: context.playerAircraft,
      callsign: "You",
      position,
      headingRad: section.headingRad,
      ammunition: section.ammunition,
      bombs: section.playerBombs,
      waypoints: section.waypoints,
    }),
    ...Array.from({ length: wingmen }, (_unused, index) =>
      buildFlight({
        id: `wingman-${index + 1}`,
        side: "friendly",
        aircraft: context.playerAircraft,
        callsign: `Wingman ${index + 1}`,
        position: {
          x: position.x + (index % 2 === 0 ? -110 : 110) * (1 + Math.floor(index / 2)),
          y: position.y,
          z: position.z + 80,
        },
        headingRad: section.headingRad,
        skill: index === 0 ? "regular" : "novice",
        ammunition: section.ammunition,
        waypoints: section.waypoints,
      }),
    ),
  ];
}

export interface HostileScreen {
  readonly count: number;
  readonly aircraft: AircraftKey;
  readonly callsign: string;
  /** The lead aeroplane's station; the rest are stepped away from it. */
  readonly anchor: Vec3;
  readonly station: Waypoint;
}

/** The scouts sent up to break the raid, or to cover it. The leader is the veteran. */
export function hostileScreen(screen: HostileScreen): MissionFlight[] {
  return Array.from({ length: screen.count }, (_unused, index) =>
    buildFlight({
      id: `hostile-${index + 1}`,
      side: "hostile",
      aircraft: screen.aircraft,
      callsign: `${screen.callsign} ${index + 1}`,
      position: {
        x: screen.anchor.x - index * 300,
        y: screen.anchor.y + index * 85,
        z: screen.anchor.z - index * 145,
      },
      headingRad: 0,
      skill: index === 0 ? "veteran" : "regular",
      waypoints: [screen.station],
    }),
  );
}

export interface RaidBriefing {
  readonly context: MissionContext;
  /** The sentence that names the raid, before `PS.EXE`'s own three. */
  readonly opening: string;
  readonly flightSize: number;
  readonly enemyCount: number;
  readonly enemyLeader: string;
  readonly enemyAircraft: AircraftKey;
  readonly ownAltitudeM: number;
  readonly enemyAltitudeM: number;
}

/**
 * A raid's briefing card: one sentence about the raid itself, then `PS.EXE`'s
 * own three-sentence template. Shared because all four missions fill exactly the
 * same slots and only the opening line differs — writing it out per generator
 * was four copies of one call drifting apart.
 */
export function raidBriefingLines(input: RaidBriefing): string[] {
  const { context } = input;
  return [
    input.opening,
    ...briefingLines({
      flightSize: input.flightSize,
      ownAircraft: context.playerAircraft,
      enemyCount: input.enemyCount,
      enemySkill: "veteran",
      enemyNation: context.enemyNation,
      enemyLeader: input.enemyLeader,
      enemyAircraft: input.enemyAircraft,
      region: context.region,
      ownAltitudeM: input.ownAltitudeM,
      enemyAltitudeM: input.enemyAltitudeM,
      weather: context.weather,
    }),
  ];
}
